import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Award, FileText, Code, Linkedin, ExternalLink, UserCircle, Wrench } from 'lucide-react';
import ApiService from '../services/api';

const AuthorDetailPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await ApiService.getAuthor(id);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Author not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return <div className="page-container"><p>Loading author…</p></div>;
  }

  if (error || !data?.author) {
    return (
      <div className="page-container">
        <Link to="/authors" className="btn btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex' }}>
          <ArrowLeft size={16} /> Back to authors
        </Link>
        <div className="auth-alert auth-alert-error">{error || 'Author not found'}</div>
      </div>
    );
  }

  const { author, articles = [], scripts = [], faqs = [], tools = [] } = data;

  return (
    <div className="page-container author-detail-page">
      <Link to="/authors" className="btn btn-secondary" style={{ marginBottom: '20px', display: 'inline-flex' }}>
        <ArrowLeft size={16} /> All authors
      </Link>

      <div className="author-detail-header">
        {author.avatar_url ? (
          <img src={author.avatar_url} alt={author.full_name} className="author-detail-avatar" />
        ) : (
          <div className="author-detail-avatar author-image-fallback">
            <UserCircle size={48} />
          </div>
        )}
        <div>
          <h1 className="page-title" style={{ marginBottom: '6px' }}>{author.full_name}</h1>
          <p className="author-title">{author.job_title || 'Contributor'}</p>
          {author.specialization && (
            <p className="author-specialization" style={{ marginTop: '8px' }}>
              <strong>Specialization:</strong> {author.specialization}
            </p>
          )}
          {author.professional_experience && (
            <div className="author-experience" style={{ marginTop: '10px' }}>
              <Award size={16} />
              <span>{author.professional_experience}</span>
            </div>
          )}
          {author.linkedin_url && (
            <a
              href={author.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="author-linkedin-link"
            >
              <Linkedin size={16} /> LinkedIn <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>

      {author.bio && (
        <section className="author-detail-section">
          <h2>About</h2>
          <p>{author.bio}</p>
        </section>
      )}

      <section className="author-detail-section">
        <h2><FileText size={20} /> Articles ({articles.length})</h2>
        {articles.length === 0 ? (
          <p className="muted-text">No articles published yet.</p>
        ) : (
          <ul className="author-content-list">
            {articles.map((item) => (
              <li key={item.id}>
                <Link to={`/article/${item.id}`}>
                  <strong>{item.title}</strong>
                  <span>{item.category}{item.subcategory ? ` · ${item.subcategory}` : ''}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="author-detail-section">
        <h2><Code size={20} /> Scripts ({scripts.length})</h2>
        {scripts.length === 0 ? (
          <p className="muted-text">No scripts published yet.</p>
        ) : (
          <ul className="author-content-list">
            {scripts.map((item) => (
              <li key={item.id}>
                <Link to={`/script/${item.id}`}>
                  <strong>{item.title}</strong>
                  <span>{item.category}{item.subcategory ? ` · ${item.subcategory}` : ''}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {tools.length > 0 && (
        <section className="author-detail-section">
          <h2><Wrench size={20} /> Tools ({tools.length})</h2>
          <ul className="author-content-list">
            {tools.map((item) => (
              <li key={item.id}>
                <Link to={`/tool/${item.id}`}>
                  <strong>{item.title}</strong>
                  <span>{item.category}{item.subcategory ? ` · ${item.subcategory}` : ''}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {faqs.length > 0 && (
        <section className="author-detail-section">
          <h2>FAQs ({faqs.length})</h2>
          <ul className="author-content-list">
            {faqs.map((item) => (
              <li key={item.id}>
                <strong>{item.question}</strong>
                <span>{item.category}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export default AuthorDetailPage;
